import { api } from './index'

type ImageUrls = {
  small: string
  default: string
  large: string
}

type ContentStyles = {
  background_color: string
  header_text_color: string
  body_text_color: string
}

type BlockStyles = {
  padding_top: number
  padding_right: number
  padding_bottom: number
  padding_left: number
}

type BorderStyles = {
  border_radius: number
  border_width: number
  border_color: string
}

type CarouselStyles = {
  item_spacing: number
}

export type LinkTarget = 'in_app_webview' | 'external_browser'

type LinkSettings = {
  url?: string
  authenticable?: boolean
  link_target?: LinkTarget
}

export type TargetingUISettings = {
  audience_type?: 'all_customers' | 'select_segment'
  segment_id?: number
  availability_type?: 'always_available' | 'set_date_range'
}

export type TargetingAttributes = {
  target_segments?: number[]
  start_at?: string
  end_at?: string
}

export type TargetingSettings = TargetingUISettings & TargetingAttributes

export type FeaturedContentBlock = {
  type: 'featured_content'
  image_urls: ImageUrls
  settings: LinkSettings & BlockStyles & BorderStyles
}

export type ExternalFormBlock = {
  type: 'external_form'
  image_urls: ImageUrls
  settings: BlockStyles &
    BorderStyles & {
      form_id?: number
    }
}

export type RecentOrdersBlock = {
  type: 'recent_orders'
  settings: BlockStyles &
    BorderStyles &
    ContentStyles &
    CarouselStyles & {
      button_text_color: string
      button_bg_color: string
    }
}

export type MenuCategoriesBlock = {
  type: 'menu_categories'
  settings: BlockStyles &
    BorderStyles &
    Omit<ContentStyles, 'body_text_color'> &
    CarouselStyles
}

export type RewardsBlock = {
  type: 'rewards'
  settings: BlockStyles &
    BorderStyles &
    ContentStyles &
    CarouselStyles & {
      button_text_color: string
      button_border_color: string
      link_color: string
    }
}

export type StoredValueBalanceBlock = {
  type: 'stored_value_balance'
  settings: BlockStyles &
    BorderStyles &
    ContentStyles & {
      chevron_color: string
    }
}

export type MissingLoyaltyBenefitsCardBlock = {
  type: 'missing_loyalty_prompt'
  settings: BlockStyles &
    BorderStyles &
    ContentStyles & {
      button_text_color: string
      button_border_color: string
      close_icon_color: string
    }
}

export type PersonalizedTextVariable =
  | 'first_name'
  | 'last_name'
  | 'points_balance'
  | 'points_currency'

export type PersonalizedTextBlock = {
  type: 'personalized_text'
  settings: BlockStyles &
    BorderStyles &
    LinkSettings & {
      text_color: string
      background_color: string
      text?: string
      text_align: 'left' | 'center' | 'right'
      fallback_behavior: 'hide' | 'show_placeholders'
      placeholder_values: Partial<
        Record<
          Exclude<
            PersonalizedTextVariable,
            'points_balance' | 'points_currency'
          >,
          string
        >
      >
    }
}

export type FeaturedContentCarouselBlock = {
  type: 'featured_content_carousel'
  featured_contents: Array<
    FeaturedContentBlock & Pick<ContentBlock, 'id' | 'position'>
  >
  settings: BlockStyles & BorderStyles & ContentStyles & CarouselStyles
}

export type GameBlock = {
  type: 'game'
  image_urls: ImageUrls
  settings: BlockStyles &
    BorderStyles & {
      brame_campaign_id?: string
    }
}

export type ContentBlock = {
  id: number
  position: number
} & TargetingSettings &
  (
    | RecentOrdersBlock
    | MenuCategoriesBlock
    | RewardsBlock
    | StoredValueBalanceBlock
    | MissingLoyaltyBenefitsCardBlock
    | FeaturedContentBlock
    | ExternalFormBlock
    | PersonalizedTextBlock
    | FeaturedContentCarouselBlock
    | GameBlock
  )

export type ContentBlockTypes = ContentBlock['type']

export type MenuCategoryStyles = {
  background_color: string
  header_text_color: string
  body_text_color: string
  link_color: string
  product_card: ProductCardStyles
  unavailable_product_card: Omit<
    ProductCardStyles,
    'border_width' | 'border_radius'
  >
}

export type ProductCardStyles = {
  background_color: string
  header_text_color: string
  body_text_color: string
  border_color: string
  border_width: number
  border_radius: number
}

export type BannerSettings = {
  background_color: string
  header_text_color: string
  body_text_color: string
  button_color: string
}

export type BannerStyles = {
  location_banner: BannerSettings
  order_placed_banner: BannerSettings
  error_banner: Omit<BannerSettings, 'header_text_color'>
}

export type MenuLayout = 'tabs' | 'continuous_scroll'

export type Settings = {
  link_color: string
} & ContentStyles &
  BannerStyles & {
    menu_category_page: MenuCategoryStyles
    menu_layout?: MenuLayout
  }

type CreateContentBlockPayload = {
  id?: number
  position: number
  type: ContentBlockTypes
  settings: ContentBlock['settings']
  source_id?: number
  source_type?: string
  file_uploads?: {
    image: {
      file_upload_id: number
    }
  }
}

type DeleteContentBlockPayload = {
  id: number
  destroy: boolean
}

type UpdateContentBlockPayload =
  | CreateContentBlockPayload
  | DeleteContentBlockPayload

export type LandingPageThemeUpdatePayload = {
  id: number
  landing_page_theme: {
    id?: number
    content_blocks: Array<UpdateContentBlockPayload>
    settings: Settings
    file_uploads?: {
      [key: string]: {
        file_upload_id: number
      }
    }
  }
}

export type LandingPageTheme = {
  id: number
  content_blocks: Array<ContentBlock>
  settings: Settings
}

export const landingPageThemeApi = api.injectEndpoints({
  endpoints: build => ({
    getLandingPageTheme: build.query<
      { app_landing_page_theme: LandingPageTheme },
      number
    >({
      query: appId => `apps/${appId}/landing_page_themes`,
      providesTags: ['LandingPageTheme'],
    }),
    updateLandingPageTheme: build.mutation<
      { app_landing_page_theme: LandingPageTheme },
      LandingPageThemeUpdatePayload
    >({
      query: payload => ({
        url: `apps/${payload.id}/landing_page_themes`,
        method: 'PUT',
        body: {
          landing_page_theme: payload.landing_page_theme,
        },
      }),
      invalidatesTags: ['LandingPageTheme'],
    }),
  }),
})
